import { Route } from '@angular/router';
import { AuthGuard } from './shared/guards/auth-guard';
import { GuestGuard } from './shared/guards/guest-guard';
import { AdminPermissionGuard } from './shared/guards/admin-permission-guard';

/**
 * Hijas del portal. La raíz ('') las sirve directamente; antes existía el
 * alias /portal, hoy reemplazado por /index como redirección canónica.
 */
const portalChildren: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/portal/views/home-view/home-view').then(
        (m) => m.HomeView
      ),
  },
  {
    path: 'diffusions',
    loadComponent: () =>
      import('./features/portal/views/diffusion-view/diffusion-view').then(
        (m) => m.DiffusionView
      ),
  },
  {
    path: 'research-works',
    loadComponent: () =>
      import('./features/portal/views/research-work-view/research-work-view').then(
        (m) => m.ResearchWorkView
      ),
  },
  {
    path: 'sustainability',
    loadComponent: () =>
      import('./features/portal/views/sustainability-view/sustainability-view').then(
        (m) => m.SustainabilityView
      ),
  },
  {
    path: 'pbg',
    loadComponent: () =>
      import('./features/portal/views/pbg-view/pbg-view').then(
        (m) => m.PbgView
      ),
  },
  {
    path: 'download',
    loadComponent: () =>
      import('./features/portal/views/download-view/download-view').then(
        (m) => m.DownloadView
      ),
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./features/portal/views/contact-view/contact-view').then(
        (m) => m.ContactView
      ),
  },
];

const loadPortalLayout = () =>
  import('./features/portal/shared/layout/portal-layout-component').then(
    (m) => m.PortalLayoutComponent
  );

export const appRoutes: Route[] = [
  // ── Portal (público) ─────────────────────────────────────
  // La raíz muestra el portal con su propio layout; /index es su alias
  // canónico (redirige a / para no duplicar contenido en dos URLs).
  {
    path: '',
    loadComponent: loadPortalLayout,
    children: portalChildren,
  },
  {
    path: 'index',
    redirectTo: '/',
    pathMatch: 'full',
  },

  // ── Autenticación ─────────────────────────────────────────
  {
    path: 'login',
    canActivate: [GuestGuard],
    loadComponent: () =>
      import('./features/auth/views/login-view/login-view').then((m) => m.LoginView),
  },
  {
    path: 'register',
    canActivate: [GuestGuard],
    loadComponent: () =>
      import('./features/auth/views/register-view/register-view').then((m) => m.RegisterView),
  },

  // ── Visor GeoMRSE-H ──────────────────────────────────────
  {
    path: 'viewer',
    // Público por diseño: permite explorar el visor sin sesión
    // (el header ofrece "Iniciar sesión" cuando no hay sesión activa).
    loadComponent: () =>
      import('./features/viewer/views/viewer-view').then((m) => m.ViewerView),
  },

  // ── Administración (feature admin, panel en /panel) ──────
  // Los hijos se agrupan por sección del menú del sidebar
  // (GET /account/roles/menus): /panel/<grupo>/<crud>.
  {
    path: 'panel',
    canActivate: [AuthGuard, AdminPermissionGuard],
    children: [
      {
        path: '',
        // Vista por defecto del panel (feature admin); los módulos se
        // abren desde el menú del sidebar (items del AdminMenuRepository).
        loadComponent: () =>
          import('./features/admin/views/admin-home-view/admin-home-view').then((m) => m.AdminHomeView),
      },
      {
        path: 'account',
        children: [
          {
            path: 'users',
            loadComponent: () =>
              import('./features/admin/views/user-view/user-view').then((m) => m.UserView),
          },
          {
            path: 'roles',
            loadComponent: () =>
              import('./features/admin/views/role-view/role-view').then((m) => m.RoleView),
          },
          {
            path: 'menus',
            loadComponent: () =>
              import('./features/admin/views/menu-view/menu-view').then((m) => m.MenuView),
          },
        ],
      },
      {
        path: 'general',
        children: [
          {
            path: 'download-requests',
            loadComponent: () =>
              import('./features/admin/views/download-request-view/download-request-view').then((m) => m.DownloadRequestView),
          },
        ],
      },
      {
        path: 'commercial',
        children: [
          {
            path: 'accreditation-documents',
            loadComponent: () =>
              import('./features/admin/views/accreditation-document-view/accreditation-document-view').then((m) => m.AccreditationDocumentView),
          },
          {
            path: 'campaigns',
            loadComponent: () =>
              import('./features/admin/views/campaign-view/campaign-view').then((m) => m.CampaignView),
          },
          {
            path: 'intervention-types',
            loadComponent: () =>
              import('./features/admin/views/intervention-type-view/intervention-type-view').then((m) => m.InterventionTypeView),
          },
          {
            path: 'species',
            loadComponent: () =>
              import('./features/admin/views/species-view/species-view').then((m) => m.SpeciesView),
          },
          {
            path: 'taxpayers',
            loadComponent: () =>
              import('./features/admin/views/taxpayer-view/taxpayer-view').then((m) => m.TaxpayerView),
          },
          {
            path: 'villages',
            loadComponent: () =>
              import('./features/admin/views/village-view/village-view').then((m) => m.VillageView),
          },
          {
            path: 'interventions',
            loadComponent: () =>
              import('./features/admin/views/intervention-view/intervention-view').then((m) => m.InterventionView),
          },
          {
            path: 'trainings',
            loadComponent: () =>
              import('./features/admin/views/training-view/training-view').then((m) => m.TrainingView),
          },
        ],
      },
      {
        path: 'technical',
        children: [
          {
            path: 'station-types',
            loadComponent: () =>
              import('./features/admin/views/station-type-view/station-type-view').then((m) => m.StationTypeView),
          },
          {
            path: 'station-states',
            loadComponent: () =>
              import('./features/admin/views/station-state-view/station-state-view').then((m) => m.StationStateView),
          },
          {
            path: 'variables',
            loadComponent: () =>
              import('./features/admin/views/variable-view/variable-view').then((m) => m.VariableView),
          },
          {
            path: 'measurements',
            loadComponent: () =>
              import('./features/admin/views/measurement-view/measurement-view').then((m) => m.MeasurementView),
          },
          {
            path: 'stations',
            loadComponent: () =>
              import('./features/admin/views/station-view/station-view').then((m) => m.StationView),
          },
          {
            path: 'period-types',
            loadComponent: () =>
              import('./features/admin/views/period-type-view/period-type-view').then((m) => m.PeriodTypeView),
          },
          {
            path: 'unit-of-measures',
            loadComponent: () =>
              import('./features/admin/views/unit-of-measure-view/unit-of-measure-view').then((m) => m.UnitOfMeasureView),
          },
          {
            path: 'nursery-structure-types',
            loadComponent: () =>
              import('./features/admin/views/nursery-structure-type-view/nursery-structure-type-view').then((m) => m.NurseryStructureTypeView),
          },
          {
            path: 'nurseries',
            loadComponent: () =>
              import('./features/admin/views/nursery-view/nursery-view').then((m) => m.NurseryView),
          },
          {
            path: 'nursery-structures',
            loadComponent: () =>
              import('./features/admin/views/nursery-structure-view/nursery-structure-view').then((m) => m.NurseryStructureView),
          },
        ],
      },
      {
        path: 'spatial',
        children: [
          {
            path: 'layers',
            loadComponent: () =>
              import('./features/admin/views/layer-view/layer-view').then((m) => m.LayerView),
          },
          {
            path: 'layer-types',
            loadComponent: () =>
              import('./features/admin/views/layer-type-view/layer-type-view').then((m) => m.LayerTypeView),
          },
          {
            path: 'districts',
            loadComponent: () =>
              import('./features/admin/views/district-view/district-view').then((m) => m.DistrictView),
          },
          {
            path: 'departments',
            loadComponent: () =>
              import('./features/admin/views/department-view/department-view').then((m) => m.DepartmentView),
          },
          {
            path: 'analysis-units',
            loadComponent: () =>
              import('./features/admin/views/analysis-unit-view/analysis-unit-view').then((m) => m.AnalysisUnitView),
          },
          {
            path: 'provinces',
            loadComponent: () =>
              import('./features/admin/views/province-view/province-view').then((m) => m.ProvinceView),
          },
          {
            path: 'watersheds',
            loadComponent: () =>
              import('./features/admin/views/watershed-view/watershed-view').then((m) => m.WatershedView),
          },
          {
            path: 'sub-watersheds',
            loadComponent: () =>
              import('./features/admin/views/sub-watershed-view/sub-watershed-view').then((m) => m.SubWatershedView),
          },
          {
            path: 'micro-watersheds',
            loadComponent: () =>
              import('./features/admin/views/micro-watershed-view/micro-watershed-view').then((m) => m.MicroWatershedView),
          },
          {
            path: 'rivers',
            loadComponent: () =>
              import('./features/admin/views/river-view/river-view').then((m) => m.RiverView),
          },
          {
            path: 'water-intakes',
            loadComponent: () =>
              import('./features/admin/views/water-intake-view/water-intake-view').then((m) => m.WaterIntakeView),
          },
        ],
      },
      {
        path: 'content',
        children: [
          {
            path: 'diffusions',
            loadComponent: () =>
              import('./features/admin/views/diffusion-view/diffusion-view').then((m) => m.DiffusionView),
          },
          {
            path: 'research-works',
            loadComponent: () =>
              import('./features/admin/views/research-work-view/research-work-view').then((m) => m.ResearchWorkView),
          },
          {
            path: 'action-types',
            loadComponent: () =>
              import('./features/admin/views/action-type-view/action-type-view').then((m) => m.ActionTypeView),
          },
          {
            path: 'sustainability',
            loadComponent: () =>
              import('./features/admin/views/sustainability-view/sustainability-view').then((m) => m.SustainabilityView),
          },
          {
            path: 'pbg',
            loadComponent: () =>
              import('./features/admin/views/pbg-view/pbg-view').then((m) => m.PbgView),
          },
        ],
      },
      {
        path: 'study-levels',
        loadComponent: () =>
          import('./features/admin/views/study-level-view/study-level-view').then((m) => m.StudyLevelView),
      },
      {
        path: 'studies',
        loadComponent: () =>
          import('./features/admin/views/study-view/study-view').then((m) => m.StudyView),
      },
    ],
  },

  // ── Estados (403 / 404) ──────────────────────────────────
  {
    path: '403',
    data: { status: 403 },
    loadComponent: () =>
      import('./shared/layout/layout-status-view').then((m) => m.LayoutStatusView),
  },
  {
    path: '404',
    data: { status: 404 },
    loadComponent: () =>
      import('./shared/layout/layout-status-view').then((m) => m.LayoutStatusView),
  },

  // ── Catch-all → 404 ──────────────────────────────────────
  {
    path: '**',
    redirectTo: '/404',
  },
];
